import { AxiosResponse } from 'axios';
import { useMutation, useQuery } from 'react-query';
import apiClient from '../apiClient/apiClient';
import {
  AddOptionsResponse,
  AddOptionsToSubrecipeBody,
  AddOrChangeRawProd,
  AddRawProdsToSubRecipe,
  DeleteSubspecRequestBody,
  EditSubrecipeDetailRequestBody, Subrecipe,
  SubrecipeDetails, SubrecipeDetailsUsageAmtRequestBody, SubrecipeDetailsUsageAmtResponse, SubrecipeRequestBody,
  UpdateSubRecipeDetailsRawProductsRequest, UpdateSubRecipeDetailsRawProductsResponse, UsageAmt
} from './subrecipe.types';

export const useSubrecipesInSpec = (requestBody: { specNo: string }, enabled: boolean = false) => {
  return useQuery<AxiosResponse<Subrecipe[]>>({
    queryKey: ['subrecipes', requestBody.specNo],
    queryFn: () => apiClient.get<Subrecipe[]>(`/sub-recipes/${requestBody.specNo}`),
    enabled,
  });
};

export const useUnusedSubRecipesForSpec = (requestBody: { specNo: number }, enabled: boolean = false) => {
  return useQuery<AxiosResponse<Subrecipe[]>>({
    queryKey: ['subrecipes-unused', requestBody.specNo],
    queryFn: () => apiClient.get<Subrecipe[]>(`/sub-recipes/${requestBody.specNo}/unused`),
    enabled
  });
};

export const useEditSubrecipe = (specNo: string) => {
  return useMutation((record: SubrecipeRequestBody) => {
    return apiClient.put(`/sub-recipes/${specNo}`, record);
  });
};

export const useSubrecipeDetails = (
  requestBody: { specNo: string; classNo: number; baseNo: number; sizeNo: number; productNo: number },
  enabled: boolean = false
) => {
  return useQuery<AxiosResponse<SubrecipeDetails[]>>({
    queryKey: [
      'subrecipe-details',
      requestBody.specNo,
      requestBody.classNo,
      requestBody.baseNo,
      requestBody.sizeNo,
      requestBody.productNo,
    ],
    queryFn: () =>
      apiClient.get<SubrecipeDetails[]>(`/sub-recipes/${requestBody.specNo}/details`, { params: requestBody }),
    enabled,
  });
};

export const useSubrecipeDetailsUsageAmt = (
  requestBody: {
    specNos: number[];
    moaClassNo: number | undefined;
    moaBaseNo?: number | null;
    moaSizeNo?: number | null;
    moaProdNo?: number | null;
    moaOptionNo?: number | null;
    moaRawProdNo?: number | null;
    usageAmount?: number | null;
  },
  enabled: boolean = false
) => {
  return useQuery<AxiosResponse<SubrecipeDetailsUsageAmtResponse[]>>({
    queryKey: [
      'sub-recipes-details',
      requestBody.specNos,
      requestBody.moaClassNo,
      requestBody.moaBaseNo,
      requestBody.moaSizeNo,
      requestBody.moaProdNo,
      requestBody.moaOptionNo,
      requestBody.moaRawProdNo,
      requestBody.usageAmount,
    ],
    queryFn: () => apiClient.post('/sub-recipes/details', requestBody),
    enabled,
  });
};

export const useSubrecipeDetailsUsageAmtOptions = (
  requestBody: {
    specNos: number[];
    moaClassNo: number | undefined;
    moaBaseNo?: number | null;
    moaSizeNo?: number | null;
    moaProdNo?: number | null;
    moaOptionNo?: number | null;
    moaRawProdNo?: number | null;
    usageAmount?: number | null;
  },
  enabled: boolean = false
) => {
  return useQuery<AxiosResponse<UsageAmt[]>>({
    queryKey: [
      'sub-recipes-details-usage-amount',
      requestBody.specNos,
      requestBody.moaClassNo,
      requestBody.moaBaseNo,
      requestBody.moaSizeNo,
      requestBody.moaProdNo,
      requestBody.moaOptionNo,
      requestBody.moaRawProdNo,
      requestBody.usageAmount,
    ],
    queryFn: () => apiClient.post('/sub-recipes/details/usage-amount', requestBody),
    enabled,
  });
};

export const useEditSubrecipeDetailUsageAmt = (specNo: string, subSpecNo: string) => {
  return useMutation((record: EditSubrecipeDetailRequestBody) => {
    return apiClient.put<any>(`/sub-recipes/${specNo}/details/${subSpecNo}`, record);
  });
};

export const useEditBulkSubrecipeDetailsUsageAmt = () => {
  return useMutation((record: SubrecipeDetailsUsageAmtRequestBody) => {
    return apiClient.put<any>(`/sub-recipes/details`, record);
  });
};

export const useAddRawProdToSubSpec = (specNo: string, subSpecNo: number | undefined) => {
  return useMutation((record: AddRawProdsToSubRecipe) => {
    return apiClient.post<any>(`/sub-recipes/${specNo}/details/${subSpecNo}`, record);
  });
};

export const useChangeRawProdForSubSpec = (specNo: string, subSpecNo: number | undefined) => {
  return useMutation((record: AddOrChangeRawProd) => {
    return apiClient.put<any>(`/sub-recipes/${specNo}/details/${subSpecNo}`, record);
  });
};

export const useAddOptionsToSubrecipe = (specNo: string, isReview: boolean, isPreview: boolean = false) => {
  return useMutation((requestBody: AddOptionsToSubrecipeBody) => {
    // return apiClient.post<AddOptionsResponse[]>(`/sub-recipes/${specNo}/options?isReview=${isReview}`, requestBody);
    return apiClient.post<AddOptionsResponse[]>(`/sub-recipes/${specNo}/options?isReview=${isReview}&isPreview=${isPreview}`, requestBody);
  });
};

export const useDeleteSubspec = () => {
  return useMutation((requestBody: DeleteSubspecRequestBody) => {
    return apiClient.delete<any>(`/sub-recipes/${requestBody.specNo}/options/${requestBody.subSpecNo}`, {
      data: requestBody.rawProdNo,
    });
  });
};

export const useUpdateSubRecipeDetailsRawProducts = () => {
  return useMutation((requestBody: UpdateSubRecipeDetailsRawProductsRequest) => {
    return apiClient.post<UpdateSubRecipeDetailsRawProductsResponse[]>(`/sub-recipes/details/raw-products`, requestBody)
  })
}

export const useUpdateSubRecipeDetailsRawProductsReview = (
    requestBody: UpdateSubRecipeDetailsRawProductsRequest
) => {
  return useQuery(
      ['UpdateSubRecipeDetailsRawProductsReview', requestBody],
      () =>
          apiClient.post<UpdateSubRecipeDetailsRawProductsResponse[]>(
              `/sub-recipes/details/raw-products`,
              { ...requestBody, review: true }
          )
  )
}